import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Ce que les deux fiches font de la même façon, derrière l'écran (chantier `nettoyage-fiche`,
 * ticket 01) : le titre en champ, la confirmation de suppression et le choix de la
 * destination, ouverts au-dessus de la fiche ; le focus qui revient au bouton qui les avait
 * ouverts ; et tout qui se replie quand une autre bulle s'ouvre (une sœur, une étape, celle
 * qu'elle attend).
 */
export interface MecaniqueDeLaFiche {
  renommer: boolean;
  setRenommer: (ouvert: boolean) => void;
  aSupprimer: boolean;
  /** Ouvrir la confirmation de suppression. */
  supprimer: () => void;
  aDeplacer: boolean;
  /** Ouvrir le choix de la destination. */
  deplacer: () => void;
  /** Les boutons qui reprennent le focus quand on renonce. */
  boutonSupprimer: RefObject<HTMLButtonElement | null>;
  boutonDeplacer: RefObject<HTMLButtonElement | null>;
  /** « Garder » ferme la confirmation : le focus revient au bouton qui l'avait ouverte. */
  garder: () => void;
  /** « Renoncer », Échap ou « Ne pas déplacer » : rien n'a bougé, le focus revient à « Déplacer ». */
  nePasDeplacer: () => void;
}

/** Le focus rendu à un bouton, une fois ce qui le couvrait refermé. */
export function rendreLeFocus(bouton: RefObject<HTMLElement | null>): void {
  requestAnimationFrame(() => bouton.current?.focus());
}

/**
 * `replier` remet à zéro ce qui est propre à chaque fiche (un réglage déplié, un ajout, le
 * panneau du téléphone) : il est appelé avec le reste à chaque nouvelle bulle. Il ne fait que
 * reposer des états, il n'a donc pas besoin d'être le dernier rendu.
 */
export function useMecaniqueDeLaFiche(bulleId: string, replier: () => void): MecaniqueDeLaFiche {
  const [renommer, setRenommer] = useState(false);
  const [aSupprimer, setASupprimer] = useState(false);
  const [aDeplacer, setADeplacer] = useState(false);
  const boutonSupprimer = useRef<HTMLButtonElement>(null);
  const boutonDeplacer = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    replier();
    setRenommer(false);
    setASupprimer(false);
    setADeplacer(false);
    // Seule une autre bulle replie : `replier` ne fait que reposer des états.
  }, [bulleId]);

  return {
    renommer,
    setRenommer,
    aSupprimer,
    supprimer: () => setASupprimer(true),
    aDeplacer,
    deplacer: () => setADeplacer(true),
    boutonSupprimer,
    boutonDeplacer,
    garder() {
      setASupprimer(false);
      rendreLeFocus(boutonSupprimer);
    },
    nePasDeplacer() {
      setADeplacer(false);
      rendreLeFocus(boutonDeplacer);
    },
  };
}
